// -- == //
import PanelBoxLayout from 'components/layout/panelBox';

import * as S from './styles';

import DropDown from 'components/form/dropDown';
import { Formik } from 'formik';
import TextInput from 'components/form/textInput';
import FieldPair from 'components/form/fieldPair';
import AddDivider from 'components/addDivider';
import RowAction from 'components/rowAction';
import Empty from 'components/empty';
import { useState } from 'react';
import { SurveyQuestionTypeEnumArray } from 'common/enums';
import { useActions } from 'common/hooks/useActions';
import { toast } from 'common/helpers/toastConfig';
import { ValidationCreateQuestionSurvey } from 'common/validations';

interface IProps {
  data: [];
  questions: any[];
  isUpdate: boolean;
}

const PenIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" />
  </svg>
);

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const QuestionIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9.2 9a3 3 0 1 1 4 2.8c-.8.3-1.2 1-1.2 1.8v.4" />
    <path d="M12 17.5h.01" />
    <circle cx="12" cy="12" r="9" />
  </svg>
);

/** `01`, `02` — the design's `.repeat-n`. */
const ordinal = (index: number) => String(index + 1).padStart(2, '0');

/** A blank choice row. The id is only ever a React key and a filter handle. */
const blankChoice = () => ({
  id: `${Date.now()}-${Math.round(Math.random() * 1e6)}`,
  en_title: '',
  ar_title: ''
});

export default function SurveyCreateQuestions({ data, questions, isUpdate }: IProps) {
  const [options, setOptions] = useState<any[]>([]);
  const { surveyQuestionAddAction, surveyQuestionDeleteAction, surveyQuestionEditAction } = useActions();

  const transformOptions = (list: any) => {
    return list.map((option: any) => ({
      id: option.id,
      translations: [
        {
          language: 'en',
          title: option.en_title
        },
        {
          language: 'ar',
          title: option.ar_title
        }
      ]
    }));
  };

  const revertTransformedData = (transformedData: any) => {
    return transformedData.map((item: any) => {
      const enTranslation = item.translations.find((t: any) => t.language === 'en');
      const arTranslation = item.translations.find((t: any) => t.language === 'ar');

      return {
        id: item.id,
        en_title: enTranslation ? enTranslation.title : '',
        ar_title: arTranslation ? arTranslation.title : ''
      };
    });
  };

  const titleOf = (item: any, language: string) => item?.translations?.find((t: any) => t.language === language)?.title;

  return (
    <S.Content>
      <PanelBoxLayout
        title="Questions"
        hint={questions?.length ? `${questions.length} added` : undefined}
        contentStyle={{ paddingTop: 'var(--s5)' }}
      >
        <Formik
          initialValues={{
            en_question: '',
            ar_question: '',
            type: '',
            typeTitle: '',
            questions: [],
            text: '',
            editId: null
          }}
          validationSchema={ValidationCreateQuestionSurvey}
          enableReinitialize
          onSubmit={(values, { setSubmitting, resetForm }) => {
            setSubmitting(false);

            /* A row the author added and then left blank is not a choice. */
            const filled = options.filter((item: any) => item.en_title?.trim() || item.ar_title?.trim());

            if (values.type === 'optional' && !filled.length) {
              toast({ model: 'error', message: 'Please add a choice!' });
              return;
            }

            if (values.editId) {
              surveyQuestionEditAction({
                type: values.type,
                translations: [
                  {
                    language: 'en',
                    title: values.en_question,
                    text: values.text
                  },
                  {
                    language: 'ar',
                    title: values.ar_question,
                    text: values.text
                  }
                ],
                text: values.text,
                options: transformOptions(filled),
                id: values.editId,
                index: questions.length,
                rate: 0,
                typeTitle: values.typeTitle
              });
            } else
              surveyQuestionAddAction({
                id: JSON.stringify(Math.random() * 67),
                translations: [
                  {
                    language: 'en',
                    title: values.en_question,
                    text: values.text
                  },
                  {
                    language: 'ar',
                    title: values.ar_question,
                    text: values.text
                  }
                ],
                options: values.type === 'optional' ? transformOptions(filled) : [],
                type: values.type,
                index: questions.length,
                rate: 0,
                typeTitle: values.typeTitle
              });
            resetForm();
            setOptions([]);
          }}
        >
          {({ values, errors, touched, handleSubmit, setValues }) => {
            const editingIndex = values.editId ? questions.findIndex((q: any) => q.id === values.editId) : -1;

            return (
              <S.Stack>
                {/*
                 * The questions already on the survey, as the design's
                 * numbered repeatable items. This was a second panel beside
                 * the composer headed "Questions List", so the thing being
                 * built and the thing being edited sat in two different
                 * columns and neither was numbered.
                 */}
                {questions?.map((item: any, index: number) => (
                  <S.RepeatItem key={item.id} className={item.id === values.editId ? 'editing' : ''}>
                    <S.RepeatHead>
                      <S.RepeatN>{ordinal(index)}</S.RepeatN>
                      <S.RepeatType>{item.typeTitle}</S.RepeatType>
                      <S.RepeatActions>
                        <RowAction
                          label={`Edit question ${ordinal(index)}`}
                          disabled={isUpdate}
                          onClick={() => {
                            setValues({
                              ...values,
                              en_question: titleOf(item, 'en'),
                              ar_question: titleOf(item, 'ar'),
                              type: item.type,
                              typeTitle: item.typeTitle,
                              editId: item.id
                            });
                            setOptions(revertTransformedData(item.options));
                          }}
                        >
                          <PenIcon />
                        </RowAction>
                        <RowAction
                          label={`Delete question ${ordinal(index)}`}
                          disabled={isUpdate}
                          onClick={() => surveyQuestionDeleteAction(item)}
                        >
                          <TrashIcon />
                        </RowAction>
                      </S.RepeatActions>
                    </S.RepeatHead>

                    <S.QuestionPair>
                      <S.QuestionText>{titleOf(item, 'en')}</S.QuestionText>
                      <S.QuestionText dir="rtl">{titleOf(item, 'ar')}</S.QuestionText>
                    </S.QuestionPair>

                    {item?.options?.length ? (
                      <>
                        <S.SubHead>Choices</S.SubHead>
                        <S.Choices>
                          {item.options.map((option: any, optionIndex: number) => (
                            <S.Choice key={option.id ?? optionIndex}>
                              {/* The list payload keys a saved choice `title`;
                                  a choice just built locally carries both
                                  translations. Read whichever is there. */}
                              {option?.title ?? titleOf(option, 'en')}
                            </S.Choice>
                          ))}
                        </S.Choices>
                      </>
                    ) : (
                      <></>
                    )}
                  </S.RepeatItem>
                ))}

                {!questions?.length ? (
                  <Empty
                    size="sm"
                    icon={<QuestionIcon />}
                    title="No questions yet"
                    text="Write the first one below. A survey cannot be published without at least one question."
                  />
                ) : (
                  <></>
                )}

                {/* The composer — the block being written, or the one being edited. */}
                <S.RepeatItem className="composer">
                  <S.RepeatHead>
                    <S.RepeatN>{editingIndex >= 0 ? ordinal(editingIndex) : ordinal(questions?.length || 0)}</S.RepeatN>
                    <S.RepeatType className="new">{editingIndex >= 0 ? 'Editing' : 'New question'}</S.RepeatType>
                  </S.RepeatHead>

                  <S.Field>
                    <DropDown
                      label="Question type"
                      required
                      list={SurveyQuestionTypeEnumArray}
                      placeholder="Question type"
                      onChange={(e) => {
                        setValues({
                          ...values,
                          typeTitle: e.title,
                          type: e.id as any
                        });
                        /* Choosing "Multiple choice" with nothing to choose
                           from is a dead end, so the first row is there. */
                        if (e.id === 'optional' && !options.length) setOptions([blankChoice()]);
                      }}
                      defaultValue={{ title: values.typeTitle, id: values.type }}
                      key={values.type}
                      message={touched.type && (errors.type as any)}
                      error={errors.type as unknown as boolean}
                      disabled={isUpdate}
                    />
                  </S.Field>

                  <FieldPair>
                    <TextInput
                      label="Question"
                      langTag="EN"
                      required
                      name="en_question"
                      value={values.en_question}
                      onChange={(e) => {
                        setValues({ ...values, en_question: e.target.value });
                      }}
                      placeholder="How satisfied are you with the cafeteria?"
                      message={touched.en_question && (errors.en_question as any)}
                      error={errors.en_question as unknown as boolean}
                      disabled={isUpdate}
                    />

                    <TextInput
                      label="Question"
                      langTag="AR"
                      required
                      dir="rtl"
                      name="ar_question"
                      value={values.ar_question}
                      onChange={(e) => {
                        setValues({ ...values, ar_question: e.target.value });
                      }}
                      placeholder="ما مدى رضاك عن الكافتيريا؟"
                      message={touched.ar_question && (errors.ar_question as any)}
                      error={errors.ar_question as unknown as boolean}
                      disabled={isUpdate}
                    />
                  </FieldPair>

                  {values.type === 'optional' ? (
                    <>
                      <S.SubHead>Choices</S.SubHead>
                      {/*
                       * The design's repeatable rows: each choice is an EN/AR
                       * pair edited in place, with its own delete. There used
                       * to be a separate "new choice" pair above the list and
                       * an Add Option button below it, so a choice was typed
                       * in one control and then read in a different one.
                       */}
                      {options.map((choice: any, index: number) => (
                        <S.ChoiceRow key={choice.id}>
                          <FieldPair>
                            <TextInput
                              value={choice.en_title}
                              name="en_choice_title"
                              label={`Choice ${index + 1}`}
                              langTag="EN"
                              onChange={(e: any) => {
                                setOptions(
                                  options.map((item: any) =>
                                    item.id === choice.id ? { ...item, en_title: e.target.value } : item
                                  )
                                );
                              }}
                              placeholder="Very satisfied"
                            />

                            <TextInput
                              value={choice.ar_title}
                              name="ar_choice_title"
                              label={`Choice ${index + 1}`}
                              langTag="AR"
                              dir="rtl"
                              onChange={(e: any) => {
                                setOptions(
                                  options.map((item: any) =>
                                    item.id === choice.id ? { ...item, ar_title: e.target.value } : item
                                  )
                                );
                              }}
                              placeholder="راضٍ جدًا"
                            />
                          </FieldPair>
                          <S.ChoiceAction>
                            <RowAction
                              label={`Delete choice ${index + 1}`}
                              disabled={isUpdate}
                              onClick={() => setOptions(options.filter((item: any) => item.id !== choice.id))}
                            >
                              <TrashIcon />
                            </RowAction>
                          </S.ChoiceAction>
                        </S.ChoiceRow>
                      ))}

                      <AddDivider>
                        <button
                          type="button"
                          disabled={isUpdate}
                          onClick={() => setOptions([...options, blankChoice()])}
                        >
                          <PlusIcon />
                          Add choice
                        </button>
                      </AddDivider>
                    </>
                  ) : (
                    <></>
                  )}
                </S.RepeatItem>

                <AddDivider>
                  <button
                    type="button"
                    disabled={isUpdate}
                    onClick={() => {
                      if (isUpdate) return;
                      handleSubmit();
                    }}
                  >
                    <PlusIcon />
                    {editingIndex >= 0 ? 'Save question' : 'Add question'}
                  </button>
                </AddDivider>
              </S.Stack>
            );
          }}
        </Formik>
      </PanelBoxLayout>
    </S.Content>
  );
}
